import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { FiltersBar } from '@/components/Expenses/FiltersBar';
import { ResultsSummary } from '@/components/Expenses/ResultsSummary';
import { ExpenseTable } from '@/components/Expenses/ExpenseTable';
import { Pagination } from '@/components/Expenses/Pagination';
import { ExpenseModal } from '@/components/Expenses/ExpenseModal';
import { EmptyState } from '@/components/Expenses/EmptyState';
import { AddExpense } from '@/components/AddExpense/AddExpense';
import { FloatingActionButton } from '@/components/FloatingActionButton';
import { ReimburseExpenseModal } from '@/components/Modals/ReimburseExpenseModal';
import { ExpensesPageSkeleton } from '@/components/Skeleton/ExpensesPageSkeleton';
import { api, type Expense, type Reimbursement } from '@/api/client';
import { Analytics } from '@/analytics/events';
import { AccessibleDialog } from '@/components/AccessibleDialog';
import { useSubscription } from '@/hooks/useSubscription';

export default function ExpensesPage() {
  const navigate = useNavigate();
  const { expenseLimitReached } = useSubscription();
  const [searchParams, setSearchParams] = useSearchParams();

  // View state
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Filter state - initialize from URL params or defaults
  const [taxYear, setTaxYear] = useState<number | undefined>(() => {
    const yearParam = searchParams.get('taxYear');
    return yearParam ? parseInt(yearParam) : new Date().getFullYear();
  });
  const [status, setStatus] = useState<'Unreimbursed' | 'PartiallyReimbursed' | 'FullyReimbursed' | undefined>(() => {
    const statusParam = searchParams.get('status');
    return statusParam as any || undefined;
  });
  const [familyMemberId, setFamilyMemberId] = useState<string | undefined>(() => {
    return searchParams.get('familyMemberId') || undefined;
  });
  const [categoryId, setCategoryId] = useState<string | undefined>(() => {
    return searchParams.get('categoryId') || undefined;
  });
  const [searchQuery, setSearchQuery] = useState<string>(() => {
    return searchParams.get('search') || '';
  });

  // Data state
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

  // Reimbursement modal state
  const [showReimburseModal, setShowReimburseModal] = useState(false);
  const [reimburseExpense, setReimburseExpense] = useState<Expense | null>(null);
  const [editingReimbursement, setEditingReimbursement] = useState<Reimbursement | null>(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const params: any = {};
      if (taxYear) params.taxYear = taxYear;
      if (status) params.status = status;
      if (familyMemberId) params.familyMemberId = familyMemberId;

      const data = await api.getExpenses(params);
      if (data) {
        // Apply client-side filters (category and search)
        let filtered = data;

        // Category filter
        if (categoryId) {
          filtered = filtered.filter(e => e.expenseCategoryId === categoryId);
        }

        // Search filter (provider or description)
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase();
          filtered = filtered.filter(e =>
            e.provider.toLowerCase().includes(query) ||
            e.description.toLowerCase().includes(query)
          );
        }

        setExpenses(filtered);
        setTotalCount(filtered.length);
      }
    } catch (err: any) {
      setFetchError(err?.message || 'Failed to load expenses');
      setExpenses([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [taxYear, status, familyMemberId, categoryId, searchQuery]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Sync filters with URL query params
  useEffect(() => {
    const params = new URLSearchParams();
    if (taxYear) params.set('taxYear', taxYear.toString());
    if (status) params.set('status', status);
    if (familyMemberId) params.set('familyMemberId', familyMemberId);
    if (categoryId) params.set('categoryId', categoryId);
    if (searchQuery) params.set('search', searchQuery);

    setSearchParams(params, { replace: true });
  }, [taxYear, status, familyMemberId, categoryId, searchQuery, setSearchParams]);

  const handleClearFilters = () => {
    setTaxYear(undefined);
    setStatus(undefined);
    setFamilyMemberId(undefined);
    setCategoryId(undefined);
    setSearchQuery('');
    setCurrentPage(1);
  };

  // Auto-open expense modal when navigated with ?expenseId=
  useEffect(() => {
    const expenseId = searchParams.get('expenseId');
    if (expenseId && !loading && expenses.length > 0) {
      const expense = expenses.find(e => e.id === expenseId);
      if (expense) {
        setSelectedExpense(expense);
        setIsModalOpen(true);
      }
      // Clear the query param so it doesn't re-trigger
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, loading, expenses, setSearchParams]);

  const handleAddExpenseClick = () => {
    if (expenseLimitReached()) {
      toast.error('Expense Limit Reached', {
        description: 'You have reached the maximum number of expenses for your current plan.',
        action: {
          label: 'Upgrade Plan',
          onClick: () => navigate('/settings?tab=billing')
        }
      });
      return;
    }
    setShowAddExpense(true);
  };

  const handleExpenseClick = (expense: Expense) => {
    setSelectedExpense(expense);
    setIsModalOpen(true);
  };

  const handleExpenseDelete = async (expenseId: string) => {
    const expense = expenses.find(e => e.id === expenseId) || selectedExpense;
    const docCount = expense?.documents?.length || 0;

    if (docCount > 0) {
      setDeleteTarget(expense || null);
      setShowDeleteDialog(true);
      return;
    }

    if (!confirm('Are you sure you want to delete this expense?')) return;

    try {
      await api.deleteExpense(expenseId);
      Analytics.expenseDeleted()
      fetchExpenses();
      setIsModalOpen(false);
      toast.success('Expense deleted');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete expense');
    }
  };

  const handleConfirmDelete = async (deleteDocuments: boolean) => {
    if (!deleteTarget) return;
    try {
      await api.deleteExpense(deleteTarget.id, deleteDocuments);
      Analytics.expenseDeleted()
      fetchExpenses();
      setIsModalOpen(false);
      toast.success('Expense deleted');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete expense');
    } finally {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const handleOpenReimburse = (expense: Expense) => {
    setReimburseExpense(expense);
    setEditingReimbursement(null);
    setShowReimburseModal(true);
  };

  const handleEditReimbursement = (reimbursement: Reimbursement) => {
    setEditingReimbursement(reimbursement);
    setReimburseExpense(selectedExpense);
    setShowReimburseModal(true);
  };

  const handleCancelReimbursement = async (reimbursementId: string) => {
    try {
      await api.cancelReimbursement(reimbursementId);
      if (selectedExpense) {
        const updated = await api.getExpense(selectedExpense.id);
        setSelectedExpense(updated);
      }
      fetchExpenses();
      toast.success('Reimbursement cancelled');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to cancel reimbursement');
    }
  };

  const handleAddExpenseSave = async (formData: any, documentIds: string[] = []) => {
    try {
      setValidationErrors({});

      const errors: Record<string, string> = {};
      if (!formData.provider) errors.provider = 'Provider is required';
      if (!formData.description) errors.description = 'Description is required';
      if (!formData.amount) errors.amount = 'Amount is required';
      if (!formData.date) errors.date = 'Date is required';

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      const payload = {
        serviceDate: formData.date,
        provider: formData.provider,
        description: formData.description,
        amount: parseFloat(formData.amount),
        expenseCategoryId: formData.expenseCategoryId || undefined,
        familyMemberId: formData.familyMemberId || undefined,
        notes: formData.notes || undefined,
      };

      const newExpense = await api.createExpense(payload);

      // Link uploaded documents to the new expense
      if (newExpense && documentIds.length > 0) {
        let linkFailures = 0;
        for (const docId of documentIds) {
          try {
            await api.linkDocument(docId, newExpense.id);
          } catch (err) {
            console.error(`Failed to link document ${docId}:`, err);
            linkFailures++;
          }
        }
        if (linkFailures > 0) {
          toast.warning(`Expense saved, but ${linkFailures} document(s) could not be linked.`);
        }
      }

      Analytics.expenseCreated(
        formData.expenseCategoryId ?? 'uncategorized',
        documentIds.length > 0,
      )
      fetchExpenses();
      setValidationErrors({});
      setShowAddExpense(false);
      toast.success('Expense created');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create expense');
      setValidationErrors({ general: err.message || 'Failed to create expense. Please try again.' });
    }
  };

  const handleAddExpenseCancel = () => {
    setValidationErrors({});
    setShowAddExpense(false);
  };

  const totalAmount = expenses.reduce((sum, expense) => sum + expense.amount, 0);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedExpenses = expenses.slice(startIndex, endIndex);
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  if (showAddExpense) {
    return (
      <AddExpense
        onCancel={handleAddExpenseCancel}
        onSave={handleAddExpenseSave}
        onSaveAsDraft={handleAddExpenseSave}
        validationErrors={validationErrors}
      />
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Page Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-[28px] font-semibold text-[#1B365D] mb-1">Expenses</h1>
          <p className="text-[14px] text-[#718096]">Track and manage your medical expenses</p>
        </div>
        <button
          onClick={() => navigate('/expenses/import')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-[#CBD5E0] text-[#1B365D] rounded-lg text-[13px] font-semibold hover:bg-[#F7FAFC] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          Import from CSV
        </button>
      </div>

      {/* Filters Bar */}
      <FiltersBar
        taxYear={taxYear}
        status={status}
        familyMemberId={familyMemberId}
        categoryId={categoryId}
        searchQuery={searchQuery}
        onTaxYearChange={setTaxYear}
        onStatusChange={setStatus}
        onFamilyMemberChange={setFamilyMemberId}
        onCategoryChange={setCategoryId}
        onSearchChange={setSearchQuery}
        onClearFilters={handleClearFilters}
      />

      {/* Results Summary */}
      <ResultsSummary
        totalCount={totalCount}
        filteredCount={totalCount}
        totalAmount={`$${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
      />

      {/* Expense Table or Empty State */}
      {loading ? (
        <ExpensesPageSkeleton />
      ) : fetchError ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-lg border border-[#E2E8F0]">
          <p className="text-[14px] text-[#718096] mb-4">{fetchError}</p>
          <button
            onClick={fetchExpenses}
            className="bg-[#1B365D] text-white px-5 py-2 rounded-lg font-semibold hover:opacity-90 transition text-sm"
          >
            Retry
          </button>
        </div>
      ) : expenses.length === 0 ? (
        <EmptyState onAddExpense={handleAddExpenseClick} />
      ) : (
        <>
          <ExpenseTable
            expenses={paginatedExpenses}
            onExpenseClick={handleExpenseClick}
          />

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}

      {/* Floating Action Button */}
      <FloatingActionButton onClick={handleAddExpenseClick} />

      {/* Expense Detail Modal */}
      <ExpenseModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        expense={selectedExpense}
        onReimburse={handleOpenReimburse}
        onDelete={handleExpenseDelete}
        onEditReimbursement={handleEditReimbursement}
        onCancelReimbursement={handleCancelReimbursement}
      />

      {/* Reimburse Expense Modal */}
      {showReimburseModal && reimburseExpense && (
        <ReimburseExpenseModal
          expense={reimburseExpense}
          editingReimbursement={editingReimbursement}
          onClose={() => {
            setShowReimburseModal(false);
            setEditingReimbursement(null);
          }}
          onSaved={async () => {
            setShowReimburseModal(false);
            setEditingReimbursement(null);
            if (selectedExpense) {
              const updated = await api.getExpense(selectedExpense.id);
              setSelectedExpense(updated);
            }
            fetchExpenses();
          }}
        />
      )}

      {/* Delete Expense with Documents Dialog */}
      {showDeleteDialog && deleteTarget && (
        <AccessibleDialog
          isOpen={showDeleteDialog}
          onClose={() => { setShowDeleteDialog(false); setDeleteTarget(null); }}
          labelledById="delete-dialog-title"
          className="bg-white rounded-lg w-full max-w-[420px] p-6 shadow-2xl"
        >
          <h3 id="delete-dialog-title" className="text-[18px] font-semibold text-[#1B365D] mb-2">Delete Expense</h3>
          <p className="text-[14px] text-[#4A5568] mb-1">
            This expense has <span className="font-semibold">{deleteTarget.documents?.length || 0}</span> linked {(deleteTarget.documents?.length || 0) === 1 ? 'document' : 'documents'}.
          </p>
          <p className="text-[13px] text-[#718096] mb-5">
            Would you like to permanently delete the documents, or return them to your document library?
          </p>
          <div className="space-y-2">
            <button
              onClick={() => handleConfirmDelete(false)}
              className="w-full text-left px-4 py-3 rounded-lg border border-[#E2E8F0] hover:border-[#1B365D] hover:bg-[#F7FAFC] transition-colors"
            >
              <div className="text-[14px] font-medium text-[#1B365D]">Return to library</div>
              <div className="text-[12px] text-[#718096]">Documents stay in your library for future use</div>
            </button>
            <button
              onClick={() => handleConfirmDelete(true)}
              className="w-full text-left px-4 py-3 rounded-lg border border-red-200 hover:border-red-400 hover:bg-red-50 transition-colors"
            >
              <div className="text-[14px] font-medium text-red-600">Delete everything</div>
              <div className="text-[12px] text-[#718096]">Permanently delete the expense and all linked documents</div>
            </button>
            <button
              onClick={() => { setShowDeleteDialog(false); setDeleteTarget(null); }}
              className="w-full text-center py-2 text-[13px] text-[#718096] hover:text-[#4A5568]"
            >
              Cancel
            </button>
          </div>
        </AccessibleDialog>
      )}
    </div>
  );
}
